import { useAtom } from "jotai";
import type Konva from "konva";
import { useEffect } from "react";

import { calculateStagePosition } from "../lib/refinePositioning";
import { panAtom, zoomAtom } from "../model/state";

type UseRefineButtonSyncProps = {
  stageRef: React.RefObject<Konva.Stage | null>;
  dimensions: { width: number; height: number };
  zoomCenter?: { x: number; y: number } | undefined;
  isGestureActive: boolean;
};

export function useRefineButtonSync({
  stageRef,
  dimensions,
  zoomCenter,
  isGestureActive
}: UseRefineButtonSyncProps) {
  const [zoom] = useAtom(zoomAtom);
  const [pan] = useAtom(panAtom);

  // Sync atoms to stage when changed externally (buttons, reset)
  useEffect(() => {
    // Skip if user is actively zooming/panning with gestures
    if (isGestureActive) return;

    const stage = stageRef.current;
    if (!stage) return;

    const currentScale = stage.scaleX();
    const currentPos = stage.position();

    // Calculate target stage position based on zoom, pan, and zoom center
    const targetPosition = calculateStagePosition({ zoom, pan, dimensions, zoomCenter });

    // Only update if there's a meaningful difference
    const needsScaleUpdate = Math.abs(currentScale - zoom) > 0.001;
    const needsPositionUpdate =
      Math.abs(currentPos.x - targetPosition.x) > 0.5 ||
      Math.abs(currentPos.y - targetPosition.y) > 0.5;

    if (needsScaleUpdate) {
      stage.scale({ x: zoom, y: zoom });
    }

    if (needsPositionUpdate) {
      stage.position(targetPosition);
    }
  }, [zoom, pan, isGestureActive, dimensions.width, dimensions.height, zoomCenter, stageRef]);
}
